import React, {FC, memo, useRef} from "react";
import {FormikProps} from "formik";
import moment from "moment";
import {Modal} from "antd";
import {PosibleFormikProps} from "./EditRestrictiveBooking";
import LayoutForm from "../../../views/LayoutForm/LayoutForm";
import css from "./EditRestrictiveBookings.less";
import Button from "../../../views/Button/Button";
import i18n from "../../../services/i18n";
import {
    GeneralSettingsCard,
    MemberRestrictionCard,
    PrerequisiteCoursesCard,
    VisibilityCard,
} from "./RestrictiveBookingInputs";
import {ExpireAction} from "../../../generated/graphql";
import {deriveBookingRestrictionActive} from "../../../../shared/bookingRestriction/deriveBookingRestrictionActive";

type RestrictiveBookingFormProps = {
    formikProps: FormikProps<PosibleFormikProps>;
    submit: (values: PosibleFormikProps) => void;
    submitting: boolean;
    setActive: (active: boolean) => void;
    onDelete?: () => void;
    existingBookingRestriction?: boolean;
};

const RestrictiveBookingForm: FC<RestrictiveBookingFormProps> = ({
    formikProps,
    submit,
    submitting,
    setActive,
    onDelete,
    existingBookingRestriction,
}) => {
    // Stabile Referenz auf formikProps, damit useEffect/useCallback nicht bei jedem Render feuern
    const formikPropsRef = useRef(formikProps);

    formikPropsRef.current = formikProps;

    // Destrukturiere die spezifischen Werte, die als stabile Dependencies dienen
    const {
        startDateTime,
        endDateTime,
        membersOnly,
        prerequisiteCourses,
        expireAction,
        active: currentActive,
    } = formikProps.values;

    const restrictionHasEnded = Boolean(
        endDateTime && moment().isAfter(endDateTime),
    );

    React.useEffect(() => {
        const hasAudienceRestriction =
            membersOnly ||
            Boolean(prerequisiteCourses && prerequisiteCourses.length > 0);
        const active = deriveBookingRestrictionActive({
            activeWithoutTimeFrame: hasAudienceRestriction,
            startDateTime,
            endDateTime,
            membersOnly,
            hasPrerequisiteCourses: Boolean(
                prerequisiteCourses && prerequisiteCourses.length > 0,
            ),
            expireAction,
        });

        if (active !== currentActive) {
            formikPropsRef.current.setFieldValue("active", active);
        }
        setActive(active);
    }, [
        membersOnly,
        prerequisiteCourses,
        expireAction,
        currentActive,
        startDateTime,
        endDateTime,
        setActive,
    ]);

    const SettingsInThePast = () => {
        if (restrictionHasEnded) {
            const message =
                expireAction === ExpireAction.HideFromAll
                    ? "Der Zeitraum ist abgelaufen; der Kurs bleibt weiterhin ausgeblendet."
                    : "Die Einstellungen liegen in der Vergangenheit und sind deshalb nicht mehr aktiv.";

            return (
                <div className={css.settingsInThePast}>
                    {message}
                </div>
            );
        }

        return null;
    };

    const handleDelete = () => {
        Modal.confirm({
            title: "Buchungsbeschränkung löschen",
            content: "Möchten Sie diese Buchungsbeschränkung wirklich löschen?",
            okText: "Ja",
            cancelText: "Nein",
            onOk: onDelete,
        });
    };

    return (
        <div>
            <LayoutForm columns={1} className={css.root}>
                <SettingsInThePast />
                <GeneralSettingsCard formikProps={formikProps} />
                <VisibilityCard formikProps={formikProps} />
                <MemberRestrictionCard formikProps={formikProps} />
                <PrerequisiteCoursesCard formikProps={formikProps} />
                <div style={{display: "flex", margin: "20px", gap: "10px"}}>
                    <Button
                        block
                        type="primary"
                        onClick={async () => submit(formikProps.values)}
                        loading={submitting}
                    >
                        {i18n.containers.restrictiveBooking.RestrictiveBookingCreateEdit.save()}
                    </Button>
                    {existingBookingRestriction && (
                        <Button
                            block
                            type="danger"
                            onClick={handleDelete}
                            disabled={submitting}
                        >
                            {"Löschen"}
                        </Button>
                    )}
                </div>
            </LayoutForm>
        </div>
    );
};

export default memo(RestrictiveBookingForm);
